import { useState } from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { allyWebSocket } from '../../services/websocket';

/**
 * D6-16 (FE-1.13): persistent connection-lost banner.
 *
 * Before this, a dropped gateway websocket was only hinted at by the disabled
 * composer — once automatic reconnects were exhausted the app went silent and
 * users were stranded with no way back short of a full page reload.
 *
 * Behavior:
 * - Hidden until the websocket has connected at least once this session
 *   (`hasEverConnected`), so initial startup never flashes a scary banner.
 * - While reconnecting (automatic or manual): calm amber "Reconnecting…"
 *   status with a spinner, announced politely to screen readers.
 * - Once automatic retries are exhausted (state settles on 'disconnected'):
 *   red alert with a "Retry now" action that resets the retry budget via
 *   `allyWebSocket.reconnectNow()`.
 */
export function ConnectionBanner() {
  const connectionState = useAuthStore((state) => state.connectionState);
  const hasEverConnected = useAuthStore((state) => state.hasEverConnected);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [isRetrying, setIsRetrying] = useState(false);

  if (!isAuthenticated || !hasEverConnected || connectionState === 'connected') {
    return null;
  }

  const isReconnecting =
    isRetrying || connectionState === 'reconnecting' || connectionState === 'connecting';

  const handleRetry = () => {
    setIsRetrying(true);
    void allyWebSocket
      .reconnectNow()
      .catch(() => {
        // The service resumes its automatic retry schedule on failure; the
        // banner keeps tracking connectionState either way.
      })
      .finally(() => {
        setIsRetrying(false);
      });
  };

  if (isReconnecting) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="shrink-0 border-b border-amber-500/20 bg-amber-500/10 px-4 py-2"
      >
        <div className="flex items-center gap-2 text-sm text-amber-100">
          <RefreshCw className="h-4 w-4 animate-spin text-amber-300" aria-hidden="true" />
          <span>Connection lost. Reconnecting…</span>
        </div>
      </div>
    );
  }

  return (
    <div role="alert" className="shrink-0 border-b border-red-500/20 bg-red-500/10 px-4 py-2">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-red-200">
          <WifiOff className="h-4 w-4 shrink-0 text-red-300" aria-hidden="true" />
          <span>
            Connection lost. We couldn&apos;t reconnect — messages won&apos;t send until
            you&apos;re back online.
          </span>
        </div>
        <button
          type="button"
          onClick={handleRetry}
          className="shrink-0 rounded-lg px-2 py-1 text-xs font-medium text-red-200 transition-colors duration-150 hover:bg-red-500/10 hover:text-white"
        >
          Retry now
        </button>
      </div>
    </div>
  );
}

export default ConnectionBanner;
